import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  TrendingUp, Target, Users, IndianRupee, Calendar,
  CheckCircle, Clock, AlertTriangle, MapPin, Tag, Maximize2, X
} from 'lucide-react';

const parseValue = (val) => {
  if (!val) return 0;
  if (typeof val === 'number') return val;
  const cleanStr = val.toString().replace(/₹|,/g, '').trim();
  if (cleanStr.includes('-')) {
    return parseFloat(cleanStr.split('-')[0].trim()) || 0;
  }
  return parseFloat(cleanStr) || 0;
};

const LeadsDashboard = ({ stats, dashboardData }) => {
  const [timeRange, setTimeRange] = useState('month');
  const [expandedChart, setExpandedChart] = useState(null);

  // Get leads data
  const leads = dashboardData?.leadData || [];

  // Process leads data
  const processLeadsData = () => {
    if (!leads.length) {
      return {
        totalLeads: 0,
        totalValue: 0,
        statusBreakdown: [],
        sourceBreakdown: [],
        recentLeads: []
      };
    }

    // Calculate total value
    const totalValue = leads.reduce((sum, lead) => sum + parseValue(lead.value || lead.budget || lead.amount), 0);

    // Status breakdown
    const statusMap = {};
    leads.forEach(lead => {
      const status = lead.status || lead.stage || 'Unknown';
      statusMap[status] = (statusMap[status] || 0) + 1;
    });

    const statusBreakdown = Object.entries(statusMap).map(([name, value]) => ({
      name,
      value
    }));

    // Source breakdown
    const sourceMap = {};
    leads.forEach(lead => {
      const source = lead.source || 'Unknown';
      sourceMap[source] = (sourceMap[source] || 0) + 1;
    });

    const sourceBreakdown = Object.entries(sourceMap).map(([name, value]) => ({
      name,
      value
    }));

    // Recent leads (last 5)
    const recentLeads = [...leads]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 5);

    return {
      totalLeads: leads.length,
      totalValue,
      statusBreakdown,
      sourceBreakdown,
      recentLeads
    };
  };

  const { totalLeads, totalValue, statusBreakdown, sourceBreakdown, recentLeads } = processLeadsData();

  // Chart colors
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        {/* Total Leads */}
        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 md:p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="min-w-0 pr-2">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Total Leads</p>
              <p className="text-base sm:text-lg md:text-2xl font-bold text-gray-900 mt-0.5 sm:mt-1 truncate">{totalLeads}</p>
            </div>
            <div className="p-2 sm:p-3 rounded-lg bg-blue-50 text-blue-600 shrink-0">
              <Target className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
            </div>
          </div>
        </div>

        {/* Total Value */}
        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 md:p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="min-w-0 pr-2">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Total Value</p>
              <p className="text-base sm:text-lg md:text-2xl font-bold text-gray-900 mt-0.5 sm:mt-1 truncate" title={formatCurrency(totalValue)}>{formatCurrency(totalValue)}</p>
            </div>
            <div className="p-2 sm:p-3 rounded-lg bg-green-50 text-green-600 shrink-0">
              <IndianRupee className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
            </div>
          </div>
        </div>

        {/* Average Value */}
        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 md:p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="min-w-0 pr-2">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Avg Value</p>
              <p className="text-base sm:text-lg md:text-2xl font-bold text-gray-900 mt-0.5 sm:mt-1 truncate" title={totalLeads > 0 ? formatCurrency(totalValue / totalLeads) : formatCurrency(0)}>
                {totalLeads > 0 ? formatCurrency(totalValue / totalLeads) : formatCurrency(0)}
              </p>
            </div>
            <div className="p-2 sm:p-3 rounded-lg bg-purple-50 text-purple-600 shrink-0">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
            </div>
          </div>
        </div>

        {/* Active Leads */}
        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 md:p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="min-w-0 pr-2">
              <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Active Leads</p>
              <p className="text-base sm:text-lg md:text-2xl font-bold text-gray-900 mt-0.5 sm:mt-1 truncate">{totalLeads}</p>
            </div>
            <div className="p-2 sm:p-3 rounded-lg bg-orange-50 text-orange-600 shrink-0">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-2 sm:gap-4 md:gap-6">
        {/* Status Breakdown */}
        <div 
          onClick={() => setExpandedChart('stage')}
          className="bg-white rounded-xl shadow-sm p-2 sm:p-4 md:p-6 overflow-hidden cursor-pointer hover:shadow-md transition-all group relative"
        >
          <div className="absolute top-2 right-2 sm:top-4 sm:right-4 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block">
            <Maximize2 className="w-4 h-4 text-gray-400" />
          </div>
          <h3 className="text-xs sm:text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-4 truncate">Leads by Stage</h3>
          {statusBreakdown.length > 0 ? (
            <div className="h-32 sm:h-48 md:h-[300px] w-full flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 0, right: 30, left: 30, bottom: 0 }}>
                  <Pie
                    data={statusBreakdown}
                    cx="50%"
                    cy="50%"
                    labelLine={typeof window !== 'undefined' && window.innerWidth < 640 ? { stroke: '#9ca3af', strokeWidth: 0.5, length: 5, length2: 5 } : true}
                    label={(props) => {
                      const { cx, cy, midAngle, outerRadius, name, value } = props;
                      const RADIAN = Math.PI / 180;
                      const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
                      const radius = outerRadius + (isMobile ? 12 : 20);
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      return (
                        <text x={x} y={y} fill="#374151" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" className="text-[8px] sm:text-xs font-semibold">
                          {name}: {value}
                        </text>
                      );
                    }}
                    outerRadius={typeof window !== 'undefined' && window.innerWidth < 640 ? "40%" : "70%"}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusBreakdown.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip wrapperStyle={{ fontSize: '12px' }} />
                  <Legend wrapperStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 md:h-64 flex items-center justify-center text-gray-500 text-sm">
              No lead data available
            </div>
          )}
        </div>

        {/* Source Breakdown */}
        <div 
          onClick={() => setExpandedChart('source')}
          className="bg-white rounded-xl shadow-sm p-2 sm:p-4 md:p-6 overflow-hidden cursor-pointer hover:shadow-md transition-all group relative"
        >
          <div className="absolute top-2 right-2 sm:top-4 sm:right-4 opacity-0 group-hover:opacity-100 transition-opacity hidden sm:block">
            <Maximize2 className="w-4 h-4 text-gray-400" />
          </div>
          <h3 className="text-xs sm:text-base md:text-lg font-semibold text-gray-900 mb-2 md:mb-4 truncate">Leads by Source</h3>
          {sourceBreakdown.length > 0 ? (
            <div className="h-32 sm:h-48 md:h-[300px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sourceBreakdown} margin={{ top: 10, right: 5, left: -5, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 8 }} interval={0} />
                  <YAxis tick={{ fontSize: 8 }} width={20} />
                  <Tooltip wrapperStyle={{ fontSize: '10px' }} />
                  <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} maxBarSize={50} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 md:h-64 flex items-center justify-center text-gray-500 text-sm">
              No source data available
            </div>
          )}
        </div>
      </div>

      {/* Recent Leads */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
        <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 md:mb-4">Recent Leads</h3>
        {recentLeads.length > 0 ? (
          <div className="space-y-2">
            {recentLeads.map((lead, index) => (
              <div
                key={lead._id || index}
                className="flex flex-col sm:flex-row sm:items-center justify-between p-2 sm:p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors gap-1 sm:gap-2"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center justify-between sm:justify-start gap-1.5 mb-1 sm:mb-1.5">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-gray-900 text-xs sm:text-sm truncate">{lead.name || lead.title || 'New Lead'}</h4>
                      {(lead.status || lead.stage) && (
                        <span className="px-1.5 py-0.5 text-[9px] sm:text-[10px] font-medium rounded-full bg-blue-100 text-blue-800 shrink-0">
                          {lead.status || lead.stage}
                        </span>
                      )}
                    </div>
                    {/* Date on Mobile */}
                    <div className="text-[10px] text-gray-400 sm:hidden">
                      {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString('en-GB') : ''}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:flex sm:flex-wrap items-center gap-y-1 gap-x-3 text-[10px] sm:text-xs text-gray-600">
                    {(lead.email || lead.phone || lead.customer?.fullName) && (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Users className="w-3 h-3 text-gray-400 shrink-0" />
                        <span className="truncate">{lead.email || lead.phone || lead.customer?.fullName}</span>
                      </div>
                    )}
                    {(lead.value || lead.budget || lead.amount) ? (
                      <div className="flex items-center gap-1.5 shrink-0">
                        <IndianRupee className="w-3 h-3 text-gray-400 shrink-0" />
                        <span className="font-medium text-gray-900">{formatCurrency(parseValue(lead.value || lead.budget || lead.amount))}</span>
                      </div>
                    ) : null}
                    {lead.source && (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Tag className="w-3 h-3 text-gray-400 shrink-0" />
                        <span className="truncate">{lead.source}</span>
                      </div>
                    )}
                    {lead.location && (
                      <div className="flex items-center gap-1.5 min-w-0">
                        <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
                        <span className="truncate">{lead.location}</span>
                      </div>
                    )}
                  </div>
                </div>
                {/* Date on Desktop */}
                <div className="hidden sm:block text-[10px] sm:text-xs text-gray-500 shrink-0">
                  {lead.createdAt ? new Date(lead.createdAt).toLocaleDateString('en-GB') : ''}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 md:py-8 text-gray-500 text-sm">
            No recent leads available
          </div>
        )}
      </div>

      {/* Expanded Chart Modal */}
      {expandedChart && (
        <div 
          className="fixed inset-0 z-[100] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setExpandedChart(null)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl p-4 sm:p-6 w-full max-w-4xl relative animate-in fade-in zoom-in-95 duration-200"
            onClick={e => e.stopPropagation()}
          >
            <button 
              onClick={() => setExpandedChart(null)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-colors z-10"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
            
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-6 pr-8">
              {expandedChart === 'stage' ? 'Leads by Stage' : 'Leads by Source'}
            </h3>
            
            <div className="w-full h-[300px] sm:h-[500px]">
              {expandedChart === 'stage' ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={{ top: 20, right: 20, left: 20, bottom: 20 }}>
                    <Pie
                      data={statusBreakdown}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={typeof window !== 'undefined' && window.innerWidth < 640 ? "25%" : "65%"}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {statusBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip wrapperStyle={{ fontSize: '12px' }} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sourceBreakdown} margin={{ top: 20, right: 20, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip wrapperStyle={{ fontSize: '14px' }} cursor={{ fill: '#f8fafc' }} />
                    <Legend wrapperStyle={{ paddingTop: '20px' }} />
                    <Bar dataKey="value" name="Total Leads" fill="#3b82f6" radius={[6, 6, 0, 0]} maxBarSize={80} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsDashboard;
